// External libraries
import propTypes from "prop-types";
import { Component, createRef } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Input } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import math from "../../Mathjs/CustomMathjsInstance";
import { Invisible, setStyle } from "../../Utilities/EntryUtils";

// Signals
import { allDatasets } from "../../Common/Store/CommonSignals";
import { disabledSections, entryValues, template } from "../../Entry/Manage/EditEntrySignals";

export class CalculatedField extends Component {
  inputRef = createRef();
  constructor({
    field,
    eventIndex,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    value,
    galleryParentName,
  }) {
    super();
    const { fieldName, fieldType, properties } = field;
    this.state = {
      field: field,
      value: value,
      invisible: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.fieldType = fieldType;
    this.labelVisible = labelVisible;
    this.label = properties?.Label || `${fieldName}:`;
    this.isReadOnly = properties?.IsReadOnly;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.conditionalFormattingRules = properties?.ConditionalFormatting;
    this.expression = properties?.Expression;
    this.parameterValues = properties?.ParameterValues;
    this.parameterDatasets = properties?.ParameterDatasets;
    this.visibilityRules = properties?.VisibilityRules;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.nonZeroingHide = properties?.NonZeroingHide;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.disposeDependencyEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};

    this.setParameterValues();
  }
  static propTypes = {
    field: propTypes.shape({
      fieldName: propTypes.string.isRequired,
      fieldType: propTypes.string.isRequired,
      properties: propTypes.shape({
        Label: propTypes.string,
        IsReadOnly: propTypes.bool,
        ConditionalFormatting: propTypes.array,
        Expression: propTypes.string,
        ParameterValues: propTypes.object,
        ParameterDatasets: propTypes.array,
        VisibilityRules: propTypes.array,
        Hint: propTypes.string,
        NonZeroingHide: propTypes.bool,
      }),
    }),
    eventIndex: propTypes.number,
    handleFieldChange: propTypes.func.isRequired,
    sectionIndex: propTypes.number.isRequired,
    fieldIndex: propTypes.number.isRequired,
    labelVisible: propTypes.bool.isRequired,
    value: propTypes.string.isRequired,
    galleryParentName: propTypes.string,
  };

  //#region lifecycle functions
  componentDidMount() {
    this.handleDependencyEffect();
    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
    this.calculateExpression();
  }

  componentWillUnmount() {
    this.disposeDependencyEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled
    );
  }

  componentDidUpdate(_, prevState) {
    if (this.state.invisible && !prevState.invisible) {
      this.clearField();
    }
  }
  //#endregion lifecycle functions

  //#region custom functions
  handleStyleEffect() {
    setStyle(
      this.inputRef.current,
      "input",
      this.conditionalFormattingRules,
      this.state.value,
      this.fieldName
    );
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      this.calculateExpression();
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });
      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
      if (this.conditionalFormattingRules && entryValues.value) this.handleStyleEffect();
    });
  }

  clearField() {
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange() {
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: this.state.value },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      fieldType: this.fieldType,
      eventIndex: this.eventIndex,
    });
  }

  calculateExpression() {
    try {
      let sanitizedExpression = this.expression.replaceAll("\r", "").replaceAll("\n", "");
      const values = entryValues.value;
      const parser = math.parser();

      parser.set("split", (str, separator) => str.split(separator));

      if (typeof this.eventIndex === "number" && this.galleryParentName) {
        const galleryValues = values[this.galleryParentName];
        if (!galleryValues?.[this.eventIndex]) {
          console.log("Gallery values or event index is undefined. Skipping evaluation.");
          return;
        }
        const galleryDataRow = galleryValues[this.eventIndex];
        Object.entries(galleryDataRow).forEach(([subKey, subValue]) => {
          const sanitizedSubKey = "_" + subKey.replace(/\s/g, "");
          const regex = new RegExp(`\\b${subKey.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedSubKey);
          parser.set(sanitizedSubKey, subValue);
        });
      } else if (typeof eventIndex !== "number") {
        Object.entries(values).forEach(([key, value]) => {
          const sanitizedKey = "_" + key.replace(/\s/g, "");
          const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
          parser.set(sanitizedKey, value);
        });
      } else {
        return;
      }

      if (this.parameterValues) {
        Object.entries(this.parameterValues).forEach(([key, value]) => {
          const sanitizedKey = "_" + key.replace(/\s/g, "");
          const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
          parser.set(sanitizedKey, value);
        });
      }
      let result = parser.evaluate(sanitizedExpression) ?? "";
      if (Number.isNaN(result)) result = "";
      if (JSON.stringify(this.state.value) !== JSON.stringify(result)) {
        this.setState({ value: result }, () => {
          this.onChange();
        });
      }
    } catch (error) {
      console.log(error);
      if (this.state.value !== "") {
        this.setState({ value: "" }, () => {
          this.onChange();
        });
      }
    }
  }

  setParameterValues() {
    if (!this.parameterValues && this.parameterDatasets) {
      this.parameterDatasets.forEach((dataset) => {
        const foundDataset = allDatasets.value.find((d) => d.id === dataset);
        if (foundDataset) {
          foundDataset.values.forEach((parameter) => {
            this.parameterValues = {
              ...this.parameterValues,
              [foundDataset.datasetName.replace(/\s/g, "") +
              "_" +
              parameter.value.replace(/\s/g, "")]: parameter.filterValue,
            };
            const newTemplate = template.value;
            if (
              !newTemplate.sections[this.sectionIndex].fields[this.fieldIndex].properties
                .ParameterValues
            ) {
              newTemplate.sections[this.sectionIndex].fields[this.fieldIndex].properties = {
                ...newTemplate.sections[this.sectionIndex].fields[this.fieldIndex].properties,
                ParameterValues: {},
              };
            }
            newTemplate.sections[this.sectionIndex].fields[
              this.fieldIndex
            ].properties.ParameterValues = this.parameterValues;
            template.value = newTemplate;
          });
        }
      });
    }
  }
  //#endregion custom functions
  render() {
    if (this.state.invisible || this.nonZeroingHide) return;
    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Input
              data-testid={this.fieldName}
              id={this.fieldName}
              name={this.fieldName}
              label={this.labelVisible ? this.label : null}
              readOnly={true}
              disabled={this.isReadOnly || this.state.disabled}
              value={this.state.value}
              onChange={(e) => this.onChange(e)}
            />
            <If
              condition={
                this.fieldInformation !== undefined &&
                this.fieldInformation !== "" &&
                this.fieldInformation !== "<p></p>"
              }
            >
              <TooltipPopupIcon content={this.fieldInformation} />
            </If>
          </div>
          {this.hint && <Hint>{this.hint}</Hint>}
        </div>
      </Ripple>
    );
  }
}
